import { Button, Result, Typography } from 'antd';
import { Component, ErrorInfo, ReactNode } from 'react';

const { Paragraph, Text } = Typography;

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo });
    // Aquí puedes agregar logging a un servicio externo
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/home';
  };

  handleReload = (): void => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;

      return (
        <div className="flex items-center justify-center min-h-screen w-full bg-gray-50">
          <Result
            status="500"
            title="Algo salió mal"
            subTitle="Lo sentimos, ha ocurrido un error inesperado en la aplicación."
            extra={[
              <Button type="primary" key="home" onClick={this.handleReset}>
                Ir al Inicio
              </Button>,
              <Button key="reload" onClick={this.handleReload}>
                Recargar Página
              </Button>,
            ]}
          >
            {isDev && this.state.error && (
              <div className="mt-4 text-left max-w-2xl mx-auto">
                <Paragraph>
                  <Text strong className="text-red-600">
                    Error: {this.state.error.message}
                  </Text>
                </Paragraph>
                {this.state.errorInfo && (
                  <Paragraph>
                    <pre className="bg-gray-100 p-4 rounded text-xs overflow-auto max-h-60">{this.state.errorInfo.componentStack}</pre>
                  </Paragraph>
                )}
              </div>
            )}
          </Result>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
